/**
 * @fileoverview ElevenLabs Sync Service
 * 
 * Service for handling ElevenLabs knowledge base synchronization operations
 * including retry mechanisms, status tracking, and event emission.
 * 
 * Requirements: 4.7, 7.1, 7.2, 7.3, 7.4
 */

import { v4 as uuidv4 } from 'uuid';

/**
 * Sync operation status types
 */
export const SyncStatus = {
  PENDING: 'pending',
  SYNCING: 'syncing',
  SYNCED: 'synced',
  FAILED: 'failed',
  RETRYING: 'retrying'
};

/**
 * Sync operation types
 */
export const SyncOperationType = {
  ASSIGN: 'assign',
  UNASSIGN: 'unassign',
  UPDATE: 'update',
  BATCH_ASSIGN: 'batch_assign',
  BATCH_UNASSIGN: 'batch_unassign'
};

/**
 * ElevenLabs Sync Service Class
 * 
 * Handles all ElevenLabs knowledge base synchronization operations
 * with retry mechanisms and status tracking.
 */
class ElevenLabsSyncService {
  constructor() {
    this.apiClient = null;
    this.config = null;
    this.syncOperations = new Map();
    this.retryQueue = new Map();
    this.eventCallbacks = new Map();
    
    // Retry configuration
    this.retryConfig = {
      maxRetries: 3,
      baseDelay: 1000, // 1 second
      maxDelay: 30000, // 30 seconds
      backoffMultiplier: 2
    };
    
    // Retryable error patterns
    this.retryableErrors = [
      /timeout/i,
      /network/i,
      /connection/i,
      /rate.?limit/i,
      /temporary/i,
      /service.?unavailable/i,
      /internal.?server.?error/i,
      /502/,
      /503/,
      /504/,
      /429/
    ];
  }

  /**
   * Initialize the sync service
   * 
   * @param {Object} apiClient - API client instance
   * @param {KnowledgeBaseAssignmentConfig} config - Module configuration
   */
  initialize(apiClient, config) {
    this.apiClient = apiClient;
    this.config = config;
  }

  /**
   * Trigger sync for assignment change
   * 
   * @param {string} fileId - File ID
   * @param {string} parrotId - Parrot ID
   * @param {string} operation - Sync operation type
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Sync operation result
   */
  async triggerSync(fileId, parrotId, operation, options = {}) {
    const syncId = uuidv4();
    const syncOperation = {
      id: syncId,
      fileId,
      parrotId,
      operation,
      status: SyncStatus.PENDING,
      attempts: 0,
      createdAt: new Date(),
      updatedAt: new Date(),
      error: null,
      ...options
    };

    // Store sync operation
    this.syncOperations.set(syncId, syncOperation);

    try {
      // Emit sync started event
      this.emitSyncEvent('sync_started', syncOperation);

      // Execute sync operation
      const result = await this.executeSyncOperation(syncOperation);
      
      // Update operation status
      syncOperation.status = SyncStatus.SYNCED;
      syncOperation.updatedAt = new Date();
      syncOperation.result = result;
      
      // Emit sync completed event
      this.emitSyncEvent('sync_completed', syncOperation);
      
      return {
        success: true,
        syncId,
        operation: syncOperation,
        result
      };

    } catch (error) {
      console.error(`Sync operation ${syncId} failed:`, error);
      
      // Update operation status
      syncOperation.status = SyncStatus.FAILED;
      syncOperation.error = error.message;
      syncOperation.updatedAt = new Date();
      
      // Check if error is retryable
      if (this.isRetryableError(error) && syncOperation.attempts < this.retryConfig.maxRetries) {
        await this.scheduleRetry(syncOperation);
      } else {
        // Emit sync failed event
        this.emitSyncEvent('sync_failed', syncOperation);
      }
      
      return {
        success: false,
        syncId,
        operation: syncOperation,
        error: error.message,
        retryScheduled: this.retryQueue.has(syncId)
      };
    }
  }

  /**
   * Execute sync operation
   * 
   * @param {Object} syncOperation - Sync operation details
   * @returns {Promise<Object>} Sync result
   */
  async executeSyncOperation(syncOperation) {
    const { fileId, parrotId, operation } = syncOperation;
    
    // Update status to syncing
    syncOperation.status = SyncStatus.SYNCING;
    syncOperation.attempts++;
    syncOperation.updatedAt = new Date();
    
    // Emit sync progress event
    this.emitSyncEvent('sync_progress', syncOperation);

    switch (operation) {
      case SyncOperationType.ASSIGN:
        return await this.syncAssignment(fileId, parrotId, true);
        
      case SyncOperationType.UNASSIGN:
        return await this.syncAssignment(fileId, parrotId, false);
        
      case SyncOperationType.UPDATE:
        return await this.syncUpdate(fileId, parrotId);
        
      case SyncOperationType.BATCH_ASSIGN:
        return await this.syncBatchAssignment(syncOperation.fileIds, syncOperation.parrotIds, true);
        
      case SyncOperationType.BATCH_UNASSIGN:
        return await this.syncBatchAssignment(syncOperation.fileIds, syncOperation.parrotIds, false);
        
      default:
        throw new Error(`Unknown sync operation: ${operation}`);
    }
  }

  /**
   * Sync assignment change to ElevenLabs
   * 
   * @param {string} fileId - File ID
   * @param {string} parrotId - Parrot ID
   * @param {boolean} assign - Whether to assign or unassign
   * @returns {Promise<Object>} Sync result
   */
  async syncAssignment(fileId, parrotId, assign) {
    try {
      const endpoint = assign 
        ? '/api/knowledge-base/sync/assign'
        : '/api/knowledge-base/sync/unassign';
        
      const response = await this.apiClient.post(endpoint, {
        fileId,
        parrotId,
        context: this.config.context,
        securityLevel: this.config.securityLevel
      });

      if (!response.success) {
        throw new Error(response.error?.message || 'Sync operation failed');
      }

      return {
        operation: assign ? 'assign' : 'unassign',
        fileId,
        parrotId,
        elevenLabsDocumentId: response.elevenLabsDocumentId,
        agentId: response.agentId,
        syncedAt: new Date()
      };

    } catch (error) {
      console.error(`Failed to sync ${assign ? 'assignment' : 'unassignment'}:`, error);
      throw error;
    }
  }

  /**
   * Sync update to ElevenLabs
   * 
   * @param {string} fileId - File ID
   * @param {string} parrotId - Parrot ID
   * @returns {Promise<Object>} Sync result
   */
  async syncUpdate(fileId, parrotId) {
    try {
      const response = await this.apiClient.post('/api/knowledge-base/sync/update', {
        fileId,
        parrotId,
        context: this.config.context,
        securityLevel: this.config.securityLevel
      });

      if (!response.success) {
        throw new Error(response.error?.message || 'Sync update failed');
      }

      return {
        operation: 'update',
        fileId,
        parrotId,
        elevenLabsDocumentId: response.elevenLabsDocumentId,
        agentId: response.agentId,
        syncedAt: new Date()
      };

    } catch (error) {
      console.error('Failed to sync update:', error);
      throw error;
    }
  }

  /**
   * Sync batch assignment to ElevenLabs
   * 
   * @param {string[]} fileIds - File IDs
   * @param {string[]} parrotIds - Parrot IDs
   * @param {boolean} assign - Whether to assign or unassign
   * @returns {Promise<Object>} Sync result
   */
  async syncBatchAssignment(fileIds, parrotIds, assign) {
    try {
      const endpoint = assign 
        ? '/api/knowledge-base/sync/batch-assign'
        : '/api/knowledge-base/sync/batch-unassign';
        
      const response = await this.apiClient.post(endpoint, {
        fileIds,
        parrotIds,
        context: this.config.context,
        securityLevel: this.config.securityLevel
      });

      if (!response.success) {
        throw new Error(response.error?.message || 'Batch sync operation failed');
      }

      return {
        operation: assign ? 'batch_assign' : 'batch_unassign',
        fileIds,
        parrotIds,
        results: response.results || [],
        syncedAt: new Date()
      };

    } catch (error) {
      console.error(`Failed to sync batch ${assign ? 'assignment' : 'unassignment'}:`, error);
      throw error;
    }
  }

  /**
   * Schedule retry for failed sync operation
   * 
   * @param {Object} syncOperation - Failed sync operation
   * @returns {Promise<void>}
   */
  async scheduleRetry(syncOperation) {
    const delay = Math.min(
      this.retryConfig.baseDelay * Math.pow(this.retryConfig.backoffMultiplier, syncOperation.attempts - 1),
      this.retryConfig.maxDelay
    );

    syncOperation.status = SyncStatus.RETRYING;
    syncOperation.retryAt = new Date(Date.now() + delay);
    
    // Add to retry queue
    this.retryQueue.set(syncOperation.id, syncOperation);
    
    // Emit retry scheduled event
    this.emitSyncEvent('sync_retry_scheduled', syncOperation);
    
    // Schedule retry execution
    setTimeout(async () => {
      try {
        await this.executeRetry(syncOperation.id);
      } catch (error) {
        console.error(`Retry execution failed for ${syncOperation.id}:`, error);
      }
    }, delay);
  }

  /**
   * Execute retry for sync operation
   * 
   * @param {string} syncId - Sync operation ID
   * @returns {Promise<Object>} Retry result
   */
  async executeRetry(syncId) {
    const syncOperation = this.retryQueue.get(syncId);
    if (!syncOperation) {
      throw new Error(`Retry operation not found: ${syncId}`);
    }

    // Remove from retry queue
    this.retryQueue.delete(syncId);
    
    try {
      // Execute sync operation
      const result = await this.executeSyncOperation(syncOperation);
      
      // Update operation status
      syncOperation.status = SyncStatus.SYNCED;
      syncOperation.updatedAt = new Date();
      syncOperation.result = result;
      
      // Emit retry success event
      this.emitSyncEvent('sync_retry_success', syncOperation);
      
      return {
        success: true,
        syncId,
        operation: syncOperation,
        result
      };

    } catch (error) {
      console.error(`Retry failed for ${syncId}:`, error);
      
      // Update operation status
      syncOperation.status = SyncStatus.FAILED;
      syncOperation.error = error.message;
      syncOperation.updatedAt = new Date();
      
      // Check if we can retry again
      if (syncOperation.attempts < this.retryConfig.maxRetries) {
        await this.scheduleRetry(syncOperation);
      } else {
        // Emit final failure event
        this.emitSyncEvent('sync_retry_exhausted', syncOperation);
      }
      
      return {
        success: false,
        syncId,
        operation: syncOperation,
        error: error.message,
        retryScheduled: this.retryQueue.has(syncId)
      };
    }
  }

  /**
   * Get sync status for operation
   * 
   * @param {string} syncId - Sync operation ID
   * @returns {Object|null} Sync operation status
   */
  getSyncStatus(syncId) {
    const operation = this.syncOperations.get(syncId) || this.retryQueue.get(syncId);
    if (!operation) {
      return null;
    }

    return {
      id: operation.id,
      fileId: operation.fileId,
      parrotId: operation.parrotId,
      operation: operation.operation,
      status: operation.status,
      attempts: operation.attempts,
      createdAt: operation.createdAt,
      updatedAt: operation.updatedAt,
      error: operation.error,
      retryAt: operation.retryAt || null
    };
  }

  /**
   * Get all sync operations for file
   * 
   * @param {string} fileId - File ID
   * @returns {Object[]} Sync operations
   */
  getSyncOperationsForFile(fileId) {
    const operations = [];
    
    for (const operation of this.syncOperations.values()) {
      if (operation.fileId === fileId) {
        operations.push(this.getSyncStatus(operation.id));
      }
    }
    
    for (const operation of this.retryQueue.values()) {
      if (operation.fileId === fileId) {
        operations.push(this.getSyncStatus(operation.id));
      }
    }
    
    return operations.sort((a, b) => b.createdAt - a.createdAt);
  }

  /**
   * Get all sync operations for parrot
   * 
   * @param {string} parrotId - Parrot ID
   * @returns {Object[]} Sync operations
   */
  getSyncOperationsForParrot(parrotId) {
    const operations = [];
    
    for (const operation of this.syncOperations.values()) {
      if (operation.parrotId === parrotId) {
        operations.push(this.getSyncStatus(operation.id));
      }
    }
    
    for (const operation of this.retryQueue.values()) {
      if (operation.parrotId === parrotId) {
        operations.push(this.getSyncStatus(operation.id));
      }
    }
    
    return operations.sort((a, b) => b.createdAt - a.createdAt);
  }

  /**
   * Check if error is retryable
   * 
   * @param {Error} error - Error to check
   * @returns {boolean} Whether error is retryable
   */
  isRetryableError(error) {
    const errorMessage = error.message || '';
    
    // Check error message patterns
    const isPatternMatch = this.retryableErrors.some(pattern => pattern.test(errorMessage));
    
    // Check HTTP status codes (5xx and 429)
    const isHttpRetryable = error.status >= 500 || error.status === 429;
    
    return isPatternMatch || isHttpRetryable;
  }

  /**
   * Register event callback
   * 
   * @param {string} eventType - Event type
   * @param {Function} callback - Callback function
   */
  onSyncEvent(eventType, callback) {
    if (!this.eventCallbacks.has(eventType)) {
      this.eventCallbacks.set(eventType, new Set());
    }
    this.eventCallbacks.get(eventType).add(callback);
  }

  /**
   * Unregister event callback
   * 
   * @param {string} eventType - Event type
   * @param {Function} callback - Callback function
   */
  offSyncEvent(eventType, callback) {
    if (this.eventCallbacks.has(eventType)) {
      this.eventCallbacks.get(eventType).delete(callback);
    }
  }

  /**
   * Emit sync event
   * 
   * @param {string} eventType - Event type
   * @param {Object} syncOperation - Sync operation data
   */
  emitSyncEvent(eventType, syncOperation) {
    if (this.eventCallbacks.has(eventType)) {
      const callbacks = this.eventCallbacks.get(eventType);
      for (const callback of callbacks) {
        try {
          callback({
            type: eventType,
            syncId: syncOperation.id,
            fileId: syncOperation.fileId,
            parrotId: syncOperation.parrotId,
            operation: syncOperation.operation,
            status: syncOperation.status,
            error: syncOperation.error,
            timestamp: Date.now()
          });
        } catch (error) {
          console.error(`Error in sync event callback for ${eventType}:`, error);
        }
      }
    }
  }

  /**
   * Clear completed operations (cleanup)
   * 
   * @param {number} maxAge - Maximum age in milliseconds
   */
  clearCompletedOperations(maxAge = 24 * 60 * 60 * 1000) { // 24 hours
    const cutoff = Date.now() - maxAge;
    
    for (const [id, operation] of this.syncOperations.entries()) {
      const isCompleted = operation.status === SyncStatus.SYNCED || operation.status === SyncStatus.FAILED;
      if (isCompleted && operation.updatedAt.getTime() < cutoff) {
        this.syncOperations.delete(id);
      }
    }
  }

  /**
   * Get sync statistics
   * 
   * @returns {Object} Sync statistics
   */
  getSyncStatistics() {
    const stats = {
      total: 0,
      pending: 0,
      syncing: 0,
      synced: 0,
      failed: 0,
      retrying: 0
    };

    for (const operation of this.syncOperations.values()) {
      stats.total++;
      stats[operation.status]++;
    }

    for (const operation of this.retryQueue.values()) {
      stats.total++;
      stats[operation.status]++;
    }

    return stats;
  }
}

// Create singleton instance
export const elevenLabsSyncService = new ElevenLabsSyncService();

export default elevenLabsSyncService;
